import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpToLine, X, ListOrdered, Loader2, Clock, AlertTriangle } from "lucide-react";
import { StatusBadge, subjectMeta, fmtTime } from "@/components/Kit";
import { useAuth } from "@/context/AuthContext";
import { Mail } from "lucide-react";

const EMAIL_OPTS = [
  ["super_admin", "Super admin only"],
  ["super_admin_teacher", "Super admin + teacher who added it"],
  ["all", "All admins & teachers"],
];

function ReadyEmailSetting() {
  const { user } = useAuth();
  const [v, setV] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setV(r.data.ready_email_to)).catch(() => {}); }, []);
  const save = async (val) => {
    setV(val);
    try { await api.put("/settings", { ready_email_to: val }); toast.success("Email setting saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mb-5" data-testid="ready-email-setting">
      <Mail className="w-4 h-4 text-emerald-400" /> "Lesson ready" email goes to:
      <select data-testid="ready-email-select" value={v} disabled={user?.role !== "super_admin"} onChange={(e) => save(e.target.value)}
        className="bg-[#090D16] border border-[#1E293B] rounded-lg px-2 py-1.5 text-slate-200 outline-none focus:border-emerald-500 disabled:opacity-60">
        {EMAIL_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </div>
  );
}

const QueueRow = ({ m, onTop, onCancel }) => {
  const s = subjectMeta(m.subject);
  const running = m.status !== "queued";
  return (
    <div className="flex flex-wrap items-center gap-4 p-4 rounded-2xl bg-[#090D16] border border-[#1E293B]" data-testid={`queue-row-${m.id}`}>
      <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center font-display font-extrabold text-indigo-300" data-testid={`queue-position-${m.id}`}>#{m.position}</div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-100 truncate">{m.title}</div>
        <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span style={{ color: s.color }}>{m.grade} · {m.subject}</span>
          {!running && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> next try ~{fmtTime(m.next_try_at)}</span>}
          <span>ready by {fmtTime(m.deadline_at)}</span>
          {m.attempts ? <span>{m.attempts} attempt{m.attempts > 1 ? "s" : ""}</span> : null}
        </div>
        {m.last_error && <div className="text-xs text-amber-300/80 mt-1">{m.last_error}</div>}
      </div>
      {m.delayed ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid={`queue-delayed-${m.id}`}><AlertTriangle className="w-3 h-3" /> Delayed — waiting for Gemini</span> : <StatusBadge status={m.status} />}
      {!running && (
        <div className="flex gap-2">
          <button data-testid={`queue-top-${m.id}`} onClick={onTop} disabled={m.position === 1} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-[#111726] border border-[#1E293B] text-slate-300 hover:border-emerald-500/50 hover:text-emerald-300 transition-colors disabled:opacity-40"><ArrowUpToLine className="w-3.5 h-3.5" /> Move to top</button>
          <button data-testid={`queue-cancel-${m.id}`} onClick={onCancel} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-[#111726] border border-[#1E293B] text-slate-400 hover:border-rose-500/50 hover:text-rose-300 transition-colors"><X className="w-3.5 h-3.5" /> Cancel</button>
        </div>
      )}
    </div>
  );
};

export default function QueuePanel() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => { api.get("/queue").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  const act = async (path, ok) => {
    try { await api.post(path); toast.success(ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-6" data-testid="queue-panel">
      <div className="flex items-center gap-2 mb-1"><ListOrdered className="w-5 h-5 text-indigo-300" /><h2 className="text-xl font-bold font-display">Recording queue</h2></div>
      <p className="text-sm text-slate-500 mb-5">Queued recordings run one by one whenever Gemini is free. If Google is busy they wait and retry automatically; in the last hour before the 6-hour target they retry every 5 minutes.</p>
      <ReadyEmailSetting />
      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : (
        <div className="space-y-3">
          {items.length === 0 && <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-[#1E293B] rounded-2xl" data-testid="queue-empty">The queue is empty. Use "Queue for later" when adding a lesson.</div>}
          {items.map((m) => (
            <QueueRow key={m.id} m={m} onTop={() => act(`/queue/${m.id}/top`, "Moved to top")} onCancel={() => act(`/queue/${m.id}/cancel`, "Removed from queue")} />
          ))}
        </div>
      )}
    </div>
  );
}
